// Calculos dos paineis de relatorio e dos insights do Dashboard.
//
// Ate 23/09 estes paineis eram constantes: FUNNEL_HEALTH e INSIGHTS vazios,
// ATTRIBUTION tudo zero, e a Atribuicao escrevia "0" direto no JSX -- enquanto o
// drill-down dela exibia 684/412/188 leads por agente, numeros fixos no codigo.
// Aqui tudo sai dos dados que o store ja carrega. Funcoes puras: sem React, sem
// banco -- recebem as listas e devolvem o que a tela desenha.
//
// O que NAO da para calcular fica "—" com o motivo, nunca um numero plausivel:
// "horas economizadas" nao tem registro de tempo em lugar nenhum.

import { leadHealth } from './pipeline-health.jsx';
import { STAGES_BY_FUNNEL, STAGES_PROJETOS, ganhoNoMes, mesLocal } from './data.jsx';

// Etapas de um funil: as do banco (com o SLA real) quando o store ja as tem;
// senao a lista padrao -- o mesmo que a Gestao e o Kanban usam.
export function etapasDoStore(store) {
  return (funil) => (store && store.etapasPorFunil && store.etapasPorFunil[funil])
    || STAGES_BY_FUNNEL[funil] || STAGES_PROJETOS;
}

const ativo = (l) => !l.perdido && l.stage !== 'GANHO';

// ---- Saude do funil ------------------------------------------------------
//
// Reusa `leadHealth` (pipeline-health.jsx), a mesma regua do cartao do Kanban e
// da Gestao: parado alem de 1,8x a mediana da etapa (ou do SLA, quando falta
// amostra), e sem atividade ha 14+ dias. Uma regua so -- se o relatorio usasse
// outra, o mesmo negocio estaria "ok" no Kanban e "em risco" aqui.
//
//   abandoned = parado em nivel critico, ou sem atividade
//   friction  = parado alem do limite
//   ok        = o resto
//
// Campos-chave para "preenchimento": o minimo para alguem que nao e o dono
// retomar o negocio sem ligar para ele.
export const CAMPOS_CHAVE = [
  ['contato', (l) => !!(l.contato && String(l.contato).trim())],
  ['e-mail ou telefone', (l) => !!((l.email && String(l.email).trim()) || (l.telefone && String(l.telefone).trim()))],
  ['cliente vinculado', (l) => !!l.clienteId],
  ['valor', (l) => (l.valor || 0) > 0],
  ['próxima ação', (l) => !!l.proximaAcao && l.proximaAcao !== '—'],
];

export function camposPreenchidos(lead) {
  const ok = CAMPOS_CHAVE.filter(([, teste]) => teste(lead)).length;
  return Math.round((ok / CAMPOS_CHAVE.length) * 100);
}

export function saudeDoFunil(leads = [], etapasDe = () => STAGES_PROJETOS) {
  return leads.filter(ativo).map((l) => {
    const h = leadHealth(l, leads, etapasDe(l.funil));
    const status = (h.parado === 'danger' || h.inativo) ? 'abandoned'
      : h.parado === 'warn' ? 'friction'
        : 'ok';
    return {
      leadId: l.id,
      empresa: l.empresa,
      stage: l.stage,
      status,
      diasEstagio: h.dias ?? 0,
      // Referencia da etapa: a mediana historica quando ha amostra, senao o
      // SLA. `null` quando nao ha nenhuma das duas -- a tela mostra "—".
      mediaHistorica: h.mediana ?? h.limite ?? null,
      valor: l.valor || 0,
      camposPreenchidos: camposPreenchidos(l),
    };
  }).sort((a, b) => {
    const peso = { abandoned: 0, friction: 1, ok: 2 };
    return peso[a.status] - peso[b.status] || b.valor - a.valor;
  });
}

// ---- Atribuicao: agentes x equipe -----------------------------------------
//
// "Quem gerou valor este mes?" O que da para afirmar com os dados que existem:
//
//   agentes  -> negocios cujo ORIGINADOR e um agente de IA (originador_tipo =
//               'ia'), criados no mes; quantos deles passaram da qualificacao;
//               e que fatia do pipeline aberto eles representam.
//   equipe   -> reunioes concluidas no mes (tarefas "Reuniao"), propostas
//               enviadas no mes, negocios ganhos no mes.
//
// A atribuicao e pela ORIGEM do negocio, nao pelo "esforco" em cada etapa: o
// historico de quem agiu em cada passo (negocio_cadeia) nao chega a tela hoje.
// A tela diz isso, em vez de chamar de esforco o que e origem.
const doAgente = (l) => l.originador && l.originador.tipo === 'ia';

function passouDaQualificacao(lead, etapas) {
  const ordem = (etapas || []).map((s) => s.id);
  const iQ = ordem.indexOf('QUALIFICADO');
  if (iQ < 0) return false;
  // GANHO e a ultima etapa dos dois funis (constantes e banco), entao o indice
  // ja o poe depois da qualificacao -- sem caso especial.
  const i = ordem.indexOf(lead.stage);
  return i >= iQ;
}

export function atribuicao({ leads = [], tarefas = [], propostas = [], agentes = [], monthId, etapasDe = () => STAGES_PROJETOS }) {
  const criadosNoMes = leads.filter((l) => mesLocal(l.criadoData) === monthId);
  const prospectados = criadosNoMes.filter(doAgente);
  const qualificados = prospectados.filter((l) => passouDaQualificacao(l, etapasDe(l.funil)));
  const ganhosDosQualificados = qualificados.filter((l) => ganhoNoMes(l, monthId) || (l.stage === 'GANHO' && !l.perdido));

  const abertos = leads.filter(ativo);
  const totalAberto = abertos.reduce((s, l) => s + (l.valor || 0), 0);
  const abertoDeAgente = abertos.filter(doAgente).reduce((s, l) => s + (l.valor || 0), 0);

  const reunioes = tarefas.filter((t) => t.feito && /reuni/i.test(t.tipo || '') && mesLocal(t.feitoEm) === monthId).length;
  const propostasEnviadas = propostas.filter((p) => mesLocal(p.enviadaEm) === monthId).length;
  const fechamentos = leads.filter((l) => ganhoNoMes(l, monthId)).length;

  // Proporcao por etapa: dos negocios criados no mes que CHEGARAM a cada etapa
  // do funil de Projetos, quantos % tiveram origem em agente.
  const etapasProj = etapasDe('projetos').filter((s) => s.id !== 'ENTRADA');
  const porEtapa = etapasProj.map((st) => {
    const chegaram = criadosNoMes.filter((l) => {
      if (l.funil && l.funil !== 'projetos') return false;
      const ordem = etapasProj.map((s) => s.id);
      const i = ordem.indexOf(l.stage);
      return i >= ordem.indexOf(st.id);
    });
    const deAgente = chegaram.filter(doAgente).length;
    const agent = chegaram.length ? Math.round((deAgente / chegaram.length) * 100) : 0;
    return { stage: st.id, total: chegaram.length, agent, human: chegaram.length ? 100 - agent : 0 };
  });

  const porAgente = agentes.map((a) => {
    const deste = prospectados.filter((l) => l.originador.id === a.id);
    return {
      id: a.id,
      nome: a.name,
      leads: deste.length,
      qualificados: deste.filter((l) => passouDaQualificacao(l, etapasDe(l.funil))).length,
    };
  });

  return {
    agentes: {
      prospectados: prospectados.length,
      qualificados: qualificados.length,
      // % do valor em aberto HOJE que teve origem em agente. Pipeline e retrato
      // do agora, igual em qualquer mes (mesma regra dos relatorios).
      pctPipeline: totalAberto > 0 ? Math.round((abertoDeAgente / totalAberto) * 100) : null,
      // Dos qualificados vindos de agente, quantos % a equipe fechou.
      convPosQualificacao: qualificados.length ? Math.round((ganhosDosQualificados.length / qualificados.length) * 100) : null,
    },
    equipe: { reunioes, propostasEnviadas, fechamentos },
    porEtapa,
    porAgente,
    semDados: criadosNoMes.length === 0,
  };
}

// ---- Insights do Dashboard -------------------------------------------------
//
// i3 (repasses) e i4 (recorrentes inativos) ja eram calculados no Dashboard;
// faltava a lista existir. i1 e i2 nunca tiveram conteudo -- estes sao os dois
// sinais que o comercial consegue agir no mesmo dia.
export function negociosEmRisco(saude) {
  const risco = saude.filter((r) => r.status !== 'ok');
  return { quantidade: risco.length, valor: risco.reduce((s, r) => s + r.valor, 0) };
}

export function semProximaAcao(leads = []) {
  const abertos = leads.filter(ativo);
  const sem = abertos.filter((l) => !l.proximaAcao || l.proximaAcao === '—');
  return { quantidade: sem.length, total: abertos.length };
}

// ---- Coorte semanal --------------------------------------------------------
//
// "Dos negocios que entraram na semana X, quantos % seguem ativos OU
// convertidos X+n semanas depois" -- a definicao que a propria tela usa (a
// celula diz "ainda ativos ou convertidos"). Ou seja: NAO PERDIDOS. Ganho conta
// como retido. Semana = segunda a domingo, horario local.
//
// UMA SEMANA SO ENTRA SE TODA PERDA DELA TIVER DATA. `perdido_em` existe desde
// a 0065 e nao tem backfill: negocio perdido antes disso tem perdido=true e
// data nula, e nao da para saber em que semana saiu da coorte. Excluir so esse
// negocio inflaria a retencao (tiraria um perdido do denominador), entao a
// semana inteira fica de fora e e contada em `semBase`. As semanas antigas saem
// da janela sozinhas e a matriz se completa com o tempo.
export function inicioDaSemana(data) {
  const d = data instanceof Date ? data : new Date(data);
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x;
}

const comoData = (v) => (v instanceof Date ? v : (v ? new Date(v) : null));

export function coorteSemanal(leads = [], { hoje = new Date(), semanas = 8 } = {}) {
  const cols = Array.from({ length: semanas }, (_, j) => `S+${j}`);
  const semanaAtual = inicioDaSemana(hoje);
  const rows = [], data = [], detalhes = [];
  let semBase = 0;
  for (let k = semanas - 1; k >= 0; k--) {
    const ini = new Date(semanaAtual); ini.setDate(ini.getDate() - 7 * k);
    const fim = new Date(ini); fim.setDate(fim.getDate() + 7);
    const coorte = leads.filter((l) => {
      const c = comoData(l.criadoData);
      return c && c >= ini && c < fim;
    });
    if (!coorte.length) continue;
    if (coorte.some((l) => l.perdido && !l.perdidoEm)) { semBase++; continue; }
    const linha = [], det = [];
    for (let j = 0; j < semanas; j++) {
      const iniJ = new Date(ini); iniJ.setDate(iniJ.getDate() + 7 * j);
      if (iniJ > hoje) { linha.push(null); det.push(null); continue; }
      const fimJ = new Date(iniJ); fimJ.setDate(fimJ.getDate() + 7);
      // Perdido antes do fim da semana j = fora. Na semana em andamento isso ja
      // e "ate agora": nenhuma perda tem data no futuro.
      const ativos = coorte.filter((l) => !(l.perdido && comoData(l.perdidoEm) < fimJ));
      linha.push(Math.round((ativos.length / coorte.length) * 100));
      det.push({ total: coorte.length, ativos: ativos.length, leadIds: coorte.map((l) => l.id) });
    }
    rows.push(`Sem ${String(ini.getDate()).padStart(2, '0')}/${String(ini.getMonth() + 1).padStart(2, '0')}`);
    data.push(linha);
    detalhes.push(det);
  }
  return { rows, cols, data, detalhes, semBase };
}
